import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { alpha } from '@material-ui/core/styles';
import { useSelector, useDispatch } from 'react-redux';
import { useQuery } from 'react-fetching-library';
import { Badge } from 'components/Shared/Badge';
import RoundedButton from 'components/Shared/RoundedButton';
import CalendarPopover from 'components/FormikFields/Calendar/CalendarPopover';
import PaginationMenuSelect from 'components/FormikFields/ComplexSelect/PaginationMenuSelect';
import { getLocalizedDateWithTime } from 'helpers/localize';
import showServerError from 'helpers/showError';
import { getAccountUsersAction } from './api';
import { useClaimUploads } from './useClaimUploads';
import { getClaimUploadStatusColor, CLAIM_UPLOAD_STATUS_LABEL_KEY } from './lib';

const useStyles = makeStyles(theme => ({
  wrap: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(3),
  },
  head: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    marginBottom: theme.spacing(2),
  },
  subtitle: { color: theme.palette.text.secondary, fontSize: 13 },
  // Force a uniform header underline. A global, unscoped rule in
  // Shared/ReportViewTable/styles.css (`th:first-child { border-bottom: none }`)
  // leaks onto every table and strips the first header cell's border, so the
  // File column looked different. Override it for all header cells here.
  table: {
    '& thead th': {
      borderBottom: `1px solid ${theme.palette.divider} !important`,
    },
  },
  filters: { display: 'flex', gap: theme.spacing(2), marginBottom: theme.spacing(2), alignItems: 'flex-start' },
  filterItem: { flex: '1 1 220px', maxWidth: 280 },
  dateField: {
    position: 'relative',
    flex: '1 1 220px',
    maxWidth: 280,
    height: 40,
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 1),
    border: `1px solid ${alpha(theme.palette.text.primary, 0.23)}`,
    borderRadius: theme.shape.borderRadius,
    '&:hover': { borderColor: theme.palette.text.primary },
  },
  dateLabel: {
    position: 'absolute',
    top: -7,
    left: 8,
    padding: theme.spacing(0, 0.5),
    backgroundColor: theme.palette.background.paper,
    fontSize: 12,
    lineHeight: '14px',
    color: theme.palette.text.secondary,
    pointerEvents: 'none',
  },
  fresh: { '& > td': { backgroundColor: alpha(theme.colors.lightBlue, 0.16) } },
  empty: { color: theme.palette.text.secondary, padding: theme.spacing(2) },
  loadMore: { textAlign: 'center', marginTop: theme.spacing(2) },
  uploaderEmail: { color: theme.palette.text.secondary, fontSize: 12 },
  errorsLink: { marginLeft: theme.spacing(1), fontSize: 12, color: theme.palette.error.main },
}));

const ClaimUploadHistory = forwardRef(({ accountUuid, freshUploadId }, ref) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { en: { claimUpload } } = useSelector(state => state.i18n);
  const { items, loading, error, applyFilters, loadMore, refresh, hasMore } = useClaimUploads(accountUuid);

  useImperativeHandle(ref, () => ({ refresh }), [refresh]);

  useEffect(() => {
    if (error) showServerError(dispatch, error?.message);
  }, [error, dispatch]);

  // --- Date range filter state ---
  const [dateRange, setDateRange] = useState([null, null]);

  const handleDateChange = range => {
    if (!range || !range[0] || !range[1]) {
      setDateRange([null, null]);
      applyFilters({ fromDate: undefined, toDate: undefined });
    } else {
      setDateRange(range);
      applyFilters({
        fromDate: range[0].toISOString(),
        toDate: range[1].toISOString(),
      });
    }
  };

  // --- Uploaded-by filter ---
  const usersAction = useMemo(() => getAccountUsersAction({ accountUuid }), [accountUuid]);
  const { payload: usersPayload, query: fetchUsers } = useQuery(usersAction, false);

  useEffect(() => {
    if (accountUuid) fetchUsers();
  }, [accountUuid, fetchUsers]);

  const userOptions = useMemo(() => {
    if (!usersPayload) return [];
    const list = Array.isArray(usersPayload) ? usersPayload : usersPayload.items || [];
    return [
      { value: '', label: claimUpload.allUploaders },
      ...list.map(u => ({
        value: u.uuid || u.userUUID || u.id || '',
        label: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || u.uuid || '',
      })),
    ];
  }, [usersPayload, claimUpload.allUploaders]);

  const [selectedUserUuid, setSelectedUserUuid] = useState('');

  const handleUserChange = (_, value) => {
    setSelectedUserUuid(value);
    applyFilters({ userUUID: value || undefined });
  };

  return (
    <div className={classes.wrap}>
      <div className={classes.head}>
        <Typography variant="h6">{claimUpload.historyTitle}</Typography>
      </div>

      <div className={classes.filters}>
        <div className={classes.dateField}>
          <span className={classes.dateLabel}>{claimUpload.filterDateRange}</span>
          <CalendarPopover
            shouldRange
            date={dateRange}
            onChange={handleDateChange}
            maxDate={new Date()}
            updateAfterChanging
            shortView={false}
            shouldReduceFontSize={false}
          />
        </div>
        <div className={classes.filterItem}>
          <PaginationMenuSelect
            dataKey="userUUID"
            filterValue={selectedUserUuid}
            columnData={{ isFilterable: false, options: userOptions }}
            onChange={handleUserChange}
            label={claimUpload.filterUploadedBy}
            shouldAlwaysShowSearch
            hideEmptyValue
          />
        </div>
      </div>

      {items.length === 0 && !loading ? (
        <Typography className={classes.empty}>{claimUpload.historyEmpty}</Typography>
      ) : (
        <Table size="small" className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell>{claimUpload.colFile}</TableCell>
              <TableCell>{claimUpload.colStatus}</TableCell>
              <TableCell>{claimUpload.colImpFail}</TableCell>
              <TableCell>{claimUpload.colUploadedBy}</TableCell>
              <TableCell align="right">{claimUpload.colUploaded}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map(row => (
              <TableRow key={row.uploadId} className={row.uploadId === freshUploadId ? classes.fresh : ''}>
                <TableCell>
                  {row.originalFileUrl ? (
                    <Link href={row.originalFileUrl} target="_blank" rel="noopener">
                      {row.fileName}
                    </Link>
                  ) : (
                    row.fileName
                  )}
                  {row.failed > 0 && row.errorFileUrl && (
                    <Link
                      href={row.errorFileUrl}
                      target="_blank"
                      rel="noopener"
                      className={classes.errorsLink}
                    >
                      {claimUpload.errorsLink}
                    </Link>
                  )}
                </TableCell>
                <TableCell>
                  <Badge
                    label={claimUpload[CLAIM_UPLOAD_STATUS_LABEL_KEY[row.status]] || row.status}
                    colorType={getClaimUploadStatusColor(row.status)}
                  />
                </TableCell>
                <TableCell>
                  {row.imported}/{row.failed}
                </TableCell>
                <TableCell>
                  {row.uploadedByUserFirstName || row.uploadedByUserLastName ? (
                    <>
                      {`${row.uploadedByUserFirstName || ''} ${row.uploadedByUserLastName || ''}`.trim()}
                      {row.uploadedByUserEmail && (
                        <div className={classes.uploaderEmail}>{row.uploadedByUserEmail}</div>
                      )}
                    </>
                  ) : (
                    row.uploadedByUserEmail || '—'
                  )}
                </TableCell>
                <TableCell align="right">
                  {row.uploadId === freshUploadId
                    ? claimUpload.justNow
                    : getLocalizedDateWithTime(row.uploadedAt)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {hasMore && (
        <div className={classes.loadMore}>
          <RoundedButton variant="outlined" size="small" isLoading={loading} onClick={loadMore}>
            {claimUpload.loadMore}
          </RoundedButton>
        </div>
      )}
    </div>
  );
});

ClaimUploadHistory.displayName = 'ClaimUploadHistory';

ClaimUploadHistory.propTypes = {
  accountUuid: PropTypes.string.isRequired,
  freshUploadId: PropTypes.string,
};

ClaimUploadHistory.defaultProps = { freshUploadId: null };

export default ClaimUploadHistory;
